import { databases } from '../lib/appwrite';
import { ID, Query } from 'appwrite';

// ============================================
// EMAIL VERIFICATION SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const VERIFICATION_COLLECTION_ID = process.env.REACT_APP_APPWRITE_VERIFICATION_COLLECTION_ID;

/**
 * Request email verification - sends verification token to backend
 * @param {string} userId - User ID
 * @param {string} email - User's email
 * @returns {Promise<Object>} - Verification request result
 */
export const requestEmailVerification = async (userId, email) => {
  try {
    // In production (Vercel), this will be /api/send-verification-email
    // In development, it goes through the proxy to server.js
    const response = await fetch('/api/send-verification-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Failed to send verification email');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to request email verification:', error);
    throw error;
  }
};

/**
 * Verify email with token
 * @param {string} token - Verification token
 * @returns {Promise<Object>} - Verification result
 */
export const verifyEmailToken = async (token) => {
  try {
    // In production (Vercel), this will be /api/verify-email
    // In development, it goes through the proxy to server.js
    const response = await fetch('/api/verify-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Verification failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Email verification failed:', error);
    throw error;
  }
};

/**
 * Check if user's email is verified
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} - Verification status
 */
export const isEmailVerified = async (userId) => {
  try {
    if (!DATABASE_ID || !VERIFICATION_COLLECTION_ID) {
      console.warn('Verification collection not configured');
      return false;
    }

    const response = await databases.listDocuments(
      DATABASE_ID,
      VERIFICATION_COLLECTION_ID,
      [
        Query.equal('user_id', userId),
        Query.equal('is_verified', true),
      ]
    );

    return response.documents.length > 0;
  } catch (error) {
    console.error('Failed to check verification status:', error);
    return false;
  }
};

/**
 * Get verification status for user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} - Verification document or null
 */
export const getVerificationStatus = async (userId) => {
  try {
    if (!DATABASE_ID || !VERIFICATION_COLLECTION_ID) {
      console.warn('Verification collection not configured');
      return null;
    }

    const response = await databases.listDocuments(
      DATABASE_ID,
      VERIFICATION_COLLECTION_ID,
      [Query.equal('user_id', userId)]
    );

    return response.documents[0] || null;
  } catch (error) {
    console.error('Failed to get verification status:', error);
    return null;
  }
};

